import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AddProductFromUpcModal from './AddProductFromUpcModal';
import ScanActionModal from './ScanActionModal';
import SafeIcon from '../../common/SafeIcon';
import { playNotificationSound } from '../../common/audioUtils';
import { OPENAI_API_KEY, OPENAI_API_URL } from '../../config/api';
import * as FiIcons from 'react-icons/fi';

const { FiScan, FiPlus, FiMinus, FiAlertTriangle, FiShoppingCart, FiPackage, FiCheck, FiSearch, FiCode, FiEyeOff, FiBox, FiEdit } = FiIcons;

const validateImageUrl = (url, timeout = 5000) => {
  if (!url || typeof url !== 'string') return Promise.resolve(false);
  if (!url.startsWith('http')) return Promise.resolve(false);
  
  return new Promise((resolve) => {
    const img = new Image();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      img.src = "";
      resolve(false);
    }, timeout);

    img.onload = () => {
      if (!timedOut) {
        clearTimeout(timer);
        if (img.naturalWidth > 10 && img.naturalHeight > 10) {
          resolve(true);
        } else {
          resolve(false);
        }
      }
    };

    img.onerror = () => {
      if (!timedOut) {
        clearTimeout(timer);
        resolve(false);
      }
    };

    img.src = url;
  });
};

const ScannerMode = () => {
  const { findProductByCode, adjustInventory, pallets } = useInventory();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('add-product');
  const [scannedCode, setScannedCode] = useState('');
  const [recentScans, setRecentScans] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  
  const [selectedPalletId, setSelectedPalletId] = useState('');

  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  
  const [upcSearchData, setUpcSearchData] = useState(null);
  const [showPromptDebug, setShowPromptDebug] = useState(false);
  const [apiResponseData, setApiResponseData] = useState(null);

  const scannerInputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (scannerInputRef.current) {
      scannerInputRef.current.focus();
    }
  }, [mode, showAddProductModal, showActionModal, selectedPalletId]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (showAddProductModal || showActionModal) return;
      
      if (document.activeElement !== scannerInputRef.current) {
        scannerInputRef.current?.focus();
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (scannedCode.trim()) {
          processScan(scannedCode.trim());
          setScannedCode('');
        }
      }, 100);
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [scannedCode, mode, showAddProductModal, showActionModal, selectedPalletId]);

  const searchUPCWithOpenAI = async (upc) => {
    try {
      console.log('Starting UPC search for:', upc);
      
      // Strict check for API Key
      if (!OPENAI_API_KEY || OPENAI_API_KEY.length < 10) {
        throw new Error('OpenAI API Key is missing or invalid. Please check your .env file and restart the server.');
      }

      const systemMessage = `You are a smart product-lookup engine. Your ONLY job is: Given a UPC code, look up the corresponding product and return a verified, structured JSON object.

-------------------- SEARCH & IDENTIFICATION --------------------
1. Use your knowledge to identify the product Name, Manufacturer, Model, and Category from the UPC.
2. KEEP DESCRIPTIONS CONCISE (Max 300 characters).

-------------------- IMAGES (HIGH PRIORITY) --------------------
1. Find at least one valid, DIRECT image URL (ending in .jpg, .jpeg, .png, .webp).
2. Prefer retailer/manufacturer images.
3. If none found, return an empty array for "images".

-------------------- PRICING --------------------
1. Estimate current market price.
2. "averagePrice" must be a NUMBER (e.g. 29.99) or null.

-------------------- STRICT OUTPUT RULES --------------------
1. Return ONLY valid JSON.
2. NO Markdown (no \`\`\`json fences).
3. NO explanatory text.
4. NO placeholders.
5. The "upc" field must match the user input EXACTLY.

-------------------- REQUIRED JSON STRUCTURE --------------------
{
  "name": "Sample Product Name",
  "manufacturer": "Brand Name",
  "model": "Model-123",
  "description": "A concise description of the product.",
  "upc": "${upc}",
  "category": "Category Name",
  "averagePrice": 99.99,
  "images": ["https://example.com/image.jpg"],
  "retailers": [
    {"name": "Example Store", "price": 99.99, "url": "https://example.com/product"}
  ]
}`;

      const userMessage = `Identify product for UPC: ${upc}. Find a DIRECT image URL.`;

      const response = await fetch(OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            { role: 'system', content: systemMessage },
            { role: 'user', content: userMessage }
          ],
          max_tokens: 4096
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Parse error text if it's JSON
        let errorMessage = errorText;
        try {
          const jsonError = JSON.parse(errorText);
          if (jsonError.error && jsonError.error.message) {
            errorMessage = jsonError.error.message;
          }
        } catch (e) {
          // Ignore JSON parse error, keep original errorText
        }

        throw new Error(`OpenAI API Error: ${response.status} - ${errorMessage}`);
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content;

      setApiResponseData({
        success: true,
        content: content,
        rawResponse: data
      });

      let productData;
      try {
        let jsonString = content.replace(/```json/gi, '').replace(/```/g, '').trim();
        const firstBrace = jsonString.indexOf('{');
        const lastBrace = jsonString.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1) {
          jsonString = jsonString.substring(firstBrace, lastBrace + 1);
        }
        productData = JSON.parse(jsonString);
      } catch (e) {
        console.error('JSON Parse Error', e);
        throw new Error('Failed to parse AI response');
      }

      let validImage = '';
      const imageCandidates = Array.isArray(productData.images) ? productData.images : (productData.image ? [productData.image] : []);
      
      // Validate images
      for (const url of imageCandidates) {
        if (url) {
          const isValid = await validateImageUrl(url);
          if (isValid) {
            validImage = url;
            break;
          }
        }
      }

      let calculatedPrice = productData.averagePrice || 0;
      if (productData.retailers?.length > 0) {
        const prices = productData.retailers.map(r => r.price).filter(p => typeof p === 'number' && p > 0);
        if (prices.length > 0) {
          calculatedPrice = Math.min(...prices);
        }
      }

      return {
        ...productData,
        image: validImage,
        upc: upc,
        localSku: upc,
        localPrice: calculatedPrice,
        averagePrice: calculatedPrice
      };

    } catch (error) {
      console.error('Error searching UPC:', error);
      setApiResponseData({ error: true, message: error.message });
      // We don't throw here to allow fallback to manual entry, but returning null signals failure
      return null;
    }
  };

  const processScan = async (code) => {
    setIsScanning(true);
    const cleanCode = code.trim();

    if (!cleanCode) {
      setIsScanning(false);
      return;
    }

    if (mode === 'add-product' && !selectedPalletId) {
      playNotificationSound('error');
      toast.error("Please select a Working Pallet before scanning!");
      setIsScanning(false);
      return;
    }

    const product = findProductByCode(cleanCode, selectedPalletId);

    if (mode === 'add-product') {
      if (product) {
        if (product.pallet_id !== selectedPalletId && selectedPalletId) {
          toast('Item found on another pallet. Creating new entry for current pallet.', { icon: '📦' });
          
          const searchData = await searchUPCWithOpenAI(cleanCode);
          if (searchData) {
            playNotificationSound('success');
            setUpcSearchData(searchData);
            setShowAddProductModal(true);
            addRecentScan({
              product: { name: searchData.name || 'Unknown Product' },
              action: 'Adding to Pallet',
              timestamp: new Date(),
              icon: FiSearch,
              color: 'purple'
            });
          } else {
             // Fallback to manual entry if AI fails even for existing product re-entry
             playNotificationSound('error');
             toast("AI lookup failed. Opening manual entry.", { icon: '📝' });
             setUpcSearchData({ upc: cleanCode });
             setShowAddProductModal(true);
             addRecentScan({
               product: { name: 'New Product' },
               action: 'Manual Entry',
               timestamp: new Date(),
               icon: FiEdit,
               color: 'orange'
             });
          }
        } else {
          playNotificationSound('success');
          setScannedProduct(product);
          setShowActionModal(true);
        }
      } else {
        toast.loading('Searching product info & verifying images...', { id: 'upc-search' });
        const searchData = await searchUPCWithOpenAI(cleanCode);
        toast.dismiss('upc-search');
        
        if (searchData) {
          playNotificationSound('success');
          setUpcSearchData(searchData);
          setShowAddProductModal(true);
          addRecentScan({
            product: { name: searchData.name || 'Unknown Product' },
            action: 'Found new product',
            timestamp: new Date(),
            icon: FiSearch,
            color: 'purple'
          });
        } else {
           // AI FAILED - Fallback to manual entry
           playNotificationSound('error'); 
           toast("Product not found. Please enter details manually.", { icon: '📝' });
           
           // Open modal with just the UPC pre-filled
           setUpcSearchData({ upc: cleanCode });
           setShowAddProductModal(true);
           
           addRecentScan({
             product: { name: 'New Product' },
             action: 'Manual Entry',
             timestamp: new Date(),
             icon: FiEdit,
             color: 'orange'
           });
        }
      }
      setIsScanning(false);
      return;
    }

    if (!product) {
      playNotificationSound('error');
      toast.error(`Product not found for code: ${cleanCode}`);
      setIsScanning(false);
      return;
    }

    if (mode === 'add') {
      const success = await adjustInventory(
        product.id, 
        1, 
        'purchase', 
        'Barcode scan - inventory addition',
        user?.name || user?.email
      );
      if (success) {
        playNotificationSound('success');
        addRecentScan({
          product,
          action: 'Added 1 item',
          timestamp: new Date(),
          icon: FiPlus,
          color: 'green'
        });
      } else {
        playNotificationSound('error');
      }
    } else if (mode === 'status') {
      showQuickActions(product);
    }
    
    setIsScanning(false);
  };

  const handleActionModalAction = async (action) => {
    if (!scannedProduct) return;
    
    if (action === 'add') {
      const success = await adjustInventory(
        scannedProduct.id, 
        1, 
        'purchase', 
        'Smart Scan - Inventory Add',
        user?.name || user?.email
      );
      if (success) {
        playNotificationSound('success');
        addRecentScan({
          product: scannedProduct,
          action: 'Added 1 item',
          timestamp: new Date(),
          icon: FiPlus,
          color: 'green'
        });
      }
      setShowActionModal(false);
    } else if (action === 'sale') {
      const success = await adjustInventory(
        scannedProduct.id, 
        -1, 
        'sale', 
        'Smart Scan - Marked as Sold',
        user?.name || user?.email
      );
      if (success) {
        playNotificationSound('success');
        addRecentScan({
          product: scannedProduct,
          action: 'Marked as Sold',
          timestamp: new Date(),
          icon: FiShoppingCart,
          color: 'blue'
        });
      }
      setShowActionModal(false);
    } else if (action === 'view') {
      setShowActionModal(false);
      navigate(`/products/${scannedProduct.id}`);
    }
  };

  const showQuickActions = async (product) => {
    const success = await adjustInventory(
      product.id,
      -1,
      'sale',
      'Barcode scan - item sold',
      user?.name || user?.email
    );
    if (success) {
      playNotificationSound('success');
      addRecentScan({
        product,
        action: 'Marked as sold',
        timestamp: new Date(),
        icon: FiShoppingCart,
        color: 'blue'
      });
    } else {
      playNotificationSound('error');
    }
  };

  const addRecentScan = (scanData) => {
    setRecentScans(prev => [scanData, ...prev.slice(0, 9)]);
  };

  const modes = [
    { id: 'add-product', name: 'Smart Scan & Add', description: 'Scan to add new products or update existing ones', icon: FiSearch, color: 'purple' },
    { id: 'add', name: 'Quick Add Inventory', description: 'Instantly add +1 inventory for existing items', icon: FiPlus, color: 'green' },
    { id: 'status', name: 'Quick Status Update', description: 'Instantly mark items as Sold (-1)', icon: FiMinus, color: 'blue' }
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">InventraQ Scanner Mode</h1>
        <p className="text-gray-600 dark:text-gray-400">Fast barcode scanning with Smart Detection and AI-powered product discovery</p>
      </motion.div>

      <input
        ref={scannerInputRef}
        type="text"
        value={scannedCode}
        onChange={(e) => setScannedCode(e.target.value)}
        className="scanner-input"
        placeholder="Scanner input"
        autoFocus
      />

      {mode === 'add-product' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-xl border-2 ${selectedPalletId ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-yellow-50 border-yellow-300 dark:bg-yellow-900/20 dark:border-yellow-700 animate-pulse'}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <SafeIcon icon={FiBox} className={`h-6 w-6 mr-3 ${selectedPalletId ? 'text-blue-600' : 'text-yellow-600'}`} />
              <div>
                <h3 className={`font-bold ${selectedPalletId ? 'text-blue-900 dark:text-blue-100' : 'text-yellow-900 dark:text-yellow-100'}`}>
                  {selectedPalletId ? 'Active Working Pallet' : 'Select Working Pallet Required'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {selectedPalletId ? `Items scanned will be assigned to: ${pallets.find(p => p.id === selectedPalletId)?.pallet_number}` : 'You must select a pallet to track profitability.'}
                </p>
              </div>
            </div>
            <select
              value={selectedPalletId}
              onChange={(e) => setSelectedPalletId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">-- Select Pallet --</option>
              {pallets.filter(p => p.is_active).map(pallet => (
                <option key={pallet.id} value={pallet.id}>
                  {pallet.pallet_number} (Added: {new Date(pallet.purchase_date).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <div className="flex items-center mb-6">
            <SafeIcon icon={FiScan} className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Scanner Mode</h2>
          </div>
          <div className="space-y-3">
            {modes.map((modeOption) => (
              <button
                key={modeOption.id}
                onClick={() => setMode(modeOption.id)}
                className={`w-full flex items-center p-4 rounded-lg border-2 transition-all duration-200 ${mode === modeOption.id ? `border-${modeOption.color}-500 bg-${modeOption.color}-50 dark:bg-${modeOption.color}-900/20` : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
              >
                <SafeIcon icon={modeOption.icon} className={`h-5 w-5 mr-3 ${mode === modeOption.id ? `text-${modeOption.color}-600 dark:text-${modeOption.color}-400` : 'text-gray-400'}`} />
                <div className="text-left">
                  <p className={`font-medium ${mode === modeOption.id ? `text-${modeOption.color}-900 dark:text-${modeOption.color}-300` : 'text-gray-900 dark:text-white'}`}>
                    {modeOption.name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{modeOption.description}</p>
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Scanner Status</h2>
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${isScanning ? 'bg-blue-100 dark:bg-blue-900/30 animate-pulse' : 'bg-green-100 dark:bg-green-900/30'}`}>
              <SafeIcon icon={isScanning ? FiScan : FiCheck} className={`h-10 w-10 ${isScanning ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`} />
            </div>
            <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {isScanning ? 'Processing...' : 'Ready to Scan'}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {mode === 'add' && 'Scan a barcode to instantly add 1 item'}
              {mode === 'status' && 'Scan a barcode to instantly mark as sold'}
              {mode === 'add-product' && 'Scan any item: We\'ll detect if it\'s new or existing on the selected pallet'}
            </p>
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                Make sure your barcode scanner is in keyboard-wedge mode
              </p>
            </div>
          </div>

          {mode === 'add-product' && (
            <button
              onClick={() => setShowPromptDebug(!showPromptDebug)}
              className="mt-4 w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiCode} className="h-4 w-4 mr-2" />
              {showPromptDebug ? 'Hide' : 'Show'} API Debug Info
            </button>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Recent Scans</h2>
          <div className="space-y-3">
            {recentScans.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No recent scans</p>
            ) : (
              recentScans.map((scan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className={`p-2 rounded-full bg-${scan.color}-100 dark:bg-${scan.color}-900/30`}>
                    <SafeIcon icon={scan.icon} className={`h-4 w-4 text-${scan.color}-600 dark:text-${scan.color}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {scan.product.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {scan.action} • {scan.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {showPromptDebug && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <SafeIcon icon={FiCode} className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">API Debug</h2>
            </div>
            <button onClick={() => setShowPromptDebug(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <SafeIcon icon={FiEyeOff} className="h-5 w-5" />
            </button>
          </div>
          
          {apiResponseData && (
            <div className={`bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${apiResponseData.error ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20' : ''}`}>
              <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(apiResponseData, null, 2)}
              </pre>
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Manual Code Entry</h2>
        <div className="flex space-x-3">
          <input
            type="text"
            placeholder="Enter UPC or SKU manually"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                processScan(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <button
            onClick={(e) => {
              const input = e.target.previousElementSibling;
              if (input.value.trim()) {
                processScan(input.value.trim());
                input.value = '';
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Process
          </button>
        </div>
      </motion.div>

      {showAddProductModal && (
        <AddProductFromUpcModal 
          isOpen={showAddProductModal} 
          onClose={() => {
            setShowAddProductModal(false);
            setUpcSearchData(null);
          }}
          initialData={upcSearchData}
          preSelectedPalletId={selectedPalletId}
        />
      )}

      {showActionModal && scannedProduct && (
        <ScanActionModal 
          isOpen={showActionModal} 
          onClose={() => setShowActionModal(false)} 
          product={scannedProduct} 
          onAction={handleActionModalAction} 
        />
      )}
    </div>
  );
};

export default ScannerMode;